import React, { useEffect, useState } from 'react';

import {
  ActionIcon,
  Box,
  Button,
  Center,
  CopyButton,
  Flex,
  SegmentedControl,
  Select,
  Space,
  Switch,
  Text,
  TextInput
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconCopy, IconListTree, IconShare2, IconX } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DroxyModal, DroxyModalProps } from 'components/Droxy/DroxyModal';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { PasswordInputWithStrength } from 'components/PasswordInputWithValidation';
import { SelectItem } from 'components/SelectItem';
import { UpgradePlanButton } from 'components/UpgradePlanButton';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { AgentIcon } from 'icons/AgentIcon';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { WhiteLabelService } from 'services/white-label-service';
import { SMART_ASSISTANT_QUERY_KEY, WHITE_LABEL_DOMAINS_QUERY_KEY } from 'shared/constants/query-keys';
import { ROUTE_LINKS } from 'shared/constants/route-links';
import { NULL_WHITE_LABEL_INTEGRATION } from 'shared/constants/smart-assistant';
import { AccessLevel, SmartAssistant, WhiteLabelIntegration } from 'shared/interfaces/SmartAssistant';
import { WhiteLabelDomain, WhiteLabelSubDomain } from 'shared/interfaces/WhiteLabelDomain';
import { buildDomainUrl } from 'shared/utils/domain';
import { MAX_PASSWORD_STRENGTH, passwordStrengthValidator, requiredInputValidator } from 'shared/utils/form-validators';
import { getAccessibilityItems } from 'shared/utils/smart-assistant';
import { isNullWhiteLabelIntegration } from 'shared/utils/white-label';

type ShareLinkType = 'agent' | 'dashboard';

interface ShareConfigurationFormValues {
  accessLevel: AccessLevel;
  dashboardSharingEnabled: boolean;
  password?: string;
  whiteLabelIntegration?: WhiteLabelIntegration;
}

interface ShareSmartAssistantModalProps extends DroxyModalProps {
  smartAssistant: SmartAssistant;
  onUpdate?: (updatedSmartAssistant: SmartAssistant) => void;
}

export function ShareSmartAssistantModal({
  smartAssistant,
  onUpdate,
  ...appModalProps
}: ShareSmartAssistantModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [shareLinkType, setShareLinkType] = useState<ShareLinkType>('agent');
  const [subscription] = useSubscriptionContext();
  const enabledWhiteLabel = subscription.product.enableWhiteLabel;

  const updateSmartAssistantMutation = useMutation({
    mutationFn: SmartAssistantService.updateSmartAssistant,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.UPDATE_SHARING_CONFIGURATION');
      form.resetDirty();
      onUpdate?.(updatedSmartAssistant);
    },
    onError: () =>
      NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.UPDATE_SHARING_CONFIGURATION')
  });

  const form = useForm<ShareConfigurationFormValues>({
    initialValues: {
      accessLevel: smartAssistant?.accessLevel ?? AccessLevel.PROTECTED,
      password: '',
      whiteLabelIntegration: smartAssistant?.whiteLabelIntegration ?? '',
      dashboardSharingEnabled: smartAssistant?.dashboardSharingConfiguration?.isEnabled ?? false
    } as ShareConfigurationFormValues,
    validate: {
      accessLevel: (value) => (value !== undefined ? requiredInputValidator(value) : null),
      password: (value, values) => {
        // If initially private and no password value, no validation
        if (smartAssistant.accessLevel === AccessLevel.PRIVATE && !value) return null;

        return values.accessLevel === AccessLevel.PRIVATE && value !== undefined
          ? requiredInputValidator(value) || passwordStrengthValidator(value, MAX_PASSWORD_STRENGTH)
          : null;
      }
    }
  });

  const updateSharingConfiguration = () => {
    // Wrap into setTimeout to make sure the validation is triggered after the form is updated
    setTimeout(() => {
      const validation = form.validate();
      if (validation.hasErrors) return;
      updateSmartAssistantMutation.mutate({
        id: smartAssistant._id,
        accessLevel:
          form.values.accessLevel && form.values.accessLevel !== smartAssistant.accessLevel
            ? form.values.accessLevel
            : undefined,
        password:
          (form.values.accessLevel === AccessLevel.PRIVATE && form.values.accessLevel !== smartAssistant.accessLevel) ||
          form.values.password
            ? form.values.password
            : undefined,
        whiteLabelIntegration: form.values.whiteLabelIntegration ? form.values.whiteLabelIntegration : undefined,
        dashboardSharingConfiguration: {
          isEnabled: form.values.dashboardSharingEnabled
        }
      });
    }, 0);
  };

  const whiteLabelDomainsQuery = useQuery({
    queryFn: WhiteLabelService.getAllWhiteLabelDomains,
    queryKey: [WHITE_LABEL_DOMAINS_QUERY_KEY],
    enabled: !!enabledWhiteLabel
  });

  const whiteLabelDomainItems =
    whiteLabelDomainsQuery.data?.flatMap((domain: WhiteLabelDomain) =>
      domain.subdomains.map((subDomain: WhiteLabelSubDomain) => ({
        label: buildDomainUrl(subDomain.prefix, domain.value, false),
        value: JSON.stringify({
          whiteLabelDomain: domain._id,
          domain: domain.value,
          subDomain: subDomain.prefix
        } as WhiteLabelIntegration)
      }))
    ) || [];

  const getShareLink = () => {
    const wlIntegration = form.values.whiteLabelIntegration;
    const baseUrl =
      wlIntegration && !isNullWhiteLabelIntegration(wlIntegration)
        ? buildDomainUrl(wlIntegration.subDomain, wlIntegration.domain)
        : window.location.origin;

    return `${baseUrl}${
      shareLinkType === 'agent' ? ROUTE_LINKS.guestSmartAssistant : ROUTE_LINKS.guestSmartAssistantDashboard
    }/${smartAssistant._id}`;
  };

  // useEffect to trigger update on relevant field changes, but skip password. Password changes are updated on blur
  useEffect(() => {
    if (
      form.isDirty('accessLevel') ||
      form.isDirty('whiteLabelIntegration') ||
      form.isDirty('dashboardSharingEnabled')
    ) {
      updateSharingConfiguration();
    }
  }, [form.values.accessLevel, form.values.whiteLabelIntegration, form.values.dashboardSharingEnabled]);

  return (
    <DroxyModal
      title={t('SMART_ASSISTANT.SHARE.TITLE')}
      description={t('SMART_ASSISTANT.SHARE.DESCRIPTION')}
      titleIcon={<IconShare2 />}
      size={500}
      {...appModalProps}
      onClose={appModalProps.onClose}>
      <Flex direction="column" gap="xs">
        <Select
          label={
            <InputLabelWithInfo
              label={t('SMART_ASSISTANT.SHARE.ACCESS.TITLE')}
              infoContentTranslationKey="SMART_ASSISTANT.SHARE.ACCESS.INFO"
            />
          }
          itemComponent={SelectItem}
          data={getAccessibilityItems()}
          error={form.errors.accessLevel}
          onChange={(value) => {
            if (!value) return;
            if (value !== AccessLevel.PRIVATE) form.setFieldValue('password', '');
            form.setFieldValue('accessLevel', value as AccessLevel);
          }}
          value={form.values.accessLevel}
        />

        <Text fz="xs" color="dimmed">
          {t(`SMART_ASSISTANT.SHARE.ACCESS.${form.values.accessLevel.toUpperCase()}.INFO`)}
        </Text>

        {form.values.accessLevel === AccessLevel.PRIVATE && (
          <PasswordInputWithStrength
            showLabels={false}
            newPassword={smartAssistant.accessLevel === AccessLevel.PRIVATE}
            passwordInputProps={{
              value: form.values.password ?? '',
              onChange: form.getInputProps('password').onChange,
              onBlur: () => updateSharingConfiguration()
            }}
          />
        )}
      </Flex>

      <Space h="lg" />

      <Flex direction="row" align="end" justify="space-between" gap="xs">
        <Select
          label={
            <Flex direction="row" align="center" justify="space-between" mb={2}>
              <InputLabelWithInfo
                label={t('SMART_ASSISTANT.SHARE.WHITE_LABEL.TITLE')}
                infoContentTranslationKey="SMART_ASSISTANT.SHARE.WHITE_LABEL.INFO"
              />
              <Text
                td="underline"
                fz="xs"
                onClick={() => navigate(ROUTE_LINKS.whiteLabel)}
                sx={{
                  '&:hover': {
                    cursor: 'pointer'
                  }
                }}>
                {t('SMART_ASSISTANT.SHARE.WHITE_LABEL.USE_YOURS')}
              </Text>
            </Flex>
          }
          disabled={!enabledWhiteLabel}
          placeholder={t('SMART_ASSISTANT.SHARE.WHITE_LABEL.PLACEHOLDER')}
          itemComponent={SelectItem}
          data={whiteLabelDomainItems}
          error={form.errors.whiteLabelIntegration}
          onChange={(value) => {
            if (!value) return;
            form.setFieldValue('whiteLabelIntegration', JSON.parse(value));
          }}
          value={JSON.stringify(form.values.whiteLabelIntegration)}
          style={{ flex: 1 }}
          styles={{ label: { width: '100%' } }}
        />

        {form.values.whiteLabelIntegration && !isNullWhiteLabelIntegration(form.values.whiteLabelIntegration) && (
          <ActionIcon
            mb={4}
            ml={4}
            onClick={() => {
              form.setFieldValue('whiteLabelIntegration', NULL_WHITE_LABEL_INTEGRATION);
            }}
            color="red">
            <IconX />
          </ActionIcon>
        )}
      </Flex>

      <Space h="lg" />

      <Flex direction="column" gap={5}>
        <Flex direction="row" justify="space-between" align="end">
          <InputLabelWithInfo
            label={t('SMART_ASSISTANT.SHARE.LINK.TITLE')}
            infoContentTranslationKey="SMART_ASSISTANT.SHARE.LINK.INFO"
            fw={500}
          />
          {subscription.product.enableDashboardShareLink ? (
            <Switch
              size="xs"
              label={<Text fz="xs">{t('SMART_ASSISTANT.SHARE.LINK.DASHBOARD.ENABLE')}</Text>}
              labelPosition="left"
              checked={form.values.dashboardSharingEnabled}
              disabled={!subscription.product.enableDashboardShareLink}
              {...form.getInputProps('dashboardSharingEnabled')}
              onChange={(event) => {
                form.setFieldValue('dashboardSharingEnabled', event.target.checked);
              }}
            />
          ) : (
            <UpgradePlanButton
              showLogo
              dark
              label={t('SMART_ASSISTANT.SHARE.LINK.DASHBOARD.UPGRADE_PLAN_LABEL')}
              variant="subtle"
              size="xs"
              compact
              styles={{ label: { padding: 0, fontWeight: 400, minHeight: 15 } }}
            />
          )}
        </Flex>

        <SegmentedControl
          fullWidth
          data={[
            {
              label: (
                <Center>
                  <AgentIcon w={18} h={18} />
                  <Box ml={10}>{t('SMART_ASSISTANT.SHARE.LINK.AGENT.TITLE')}</Box>
                </Center>
              ),
              value: 'agent'
            },
            {
              label: (
                <Center>
                  <IconListTree />
                  <Box ml={10}>{t('SMART_ASSISTANT.SHARE.LINK.DASHBOARD.TITLE')}</Box>
                </Center>
              ),
              value: 'dashboard',
              disabled: !form.values.dashboardSharingEnabled || !subscription.product.enableDashboardShareLink
            }
          ]}
          value={shareLinkType}
          onChange={(value) => setShareLinkType(value as ShareLinkType)}
        />

        <Flex direction="column" gap="xs" w="100%" mt={5}>
          <Text fz="xs" color="dimmed">
            {t(`SMART_ASSISTANT.SHARE.LINK.${shareLinkType.toUpperCase()}.INFO`)}
          </Text>

          <Flex w="100%" direction="row" gap="xs" align="center">
            <TextInput readOnly value={getShareLink()} style={{ flex: 1 }} />
            <CopyButton value={getShareLink()} timeout={2000}>
              {({ copied, copy }) => (
                <ActionIcon onClick={copy} variant="subtle" color={copied ? 'green' : 'gray'}>
                  {copied ? <IconCheck /> : <IconCopy />}
                </ActionIcon>
              )}
            </CopyButton>
          </Flex>
        </Flex>
      </Flex>

      <Space h="xl" />

      <Flex justify="flex-end">
        <Button onClick={appModalProps.onClose}>{t('APP.ACTIONS.DONE')}</Button>
      </Flex>
    </DroxyModal>
  );
}
